import './theme-sandbox.css';
import { PANEL_ANIMATION_DELAY_MS } from '../constants.js';

const DESKTOP_QUERY = '(pointer: fine) and (min-width: 769px)';

const SWATCHES = [
  { label: 'Neutral', value: 'oklch(55.6% 0 0)', onColor: 'oklch(100% 0 0)' },
  { label: 'Violet', value: 'oklch(60.6% 0.25 292.717)', onColor: 'oklch(100% 0 0)' },
  { label: 'Sky', value: 'oklch(68.5% 0.169 237.323)', onColor: 'oklch(100% 0 0)' },
  { label: 'Emerald', value: 'oklch(69.6% 0.17 157.788)', onColor: 'oklch(100% 0 0)' },
  { label: 'Rose', value: 'oklch(64.5% 0.246 16.439)', onColor: 'oklch(100% 0 0)' },
  { label: 'Amber', value: 'oklch(76.9% 0.188 70.08)', onColor: 'oklch(0% 0 0)' },
];

const FONTS = [
  { label: 'Manrope', value: 'Manrope, sans-serif' },
  { label: 'JetBrains Mono', value: '"JetBrains Mono", monospace' },
  { label: 'Sora', value: 'Sora, sans-serif' },
  { label: 'Noto Serif', value: '"Noto Serif", serif' },
];

const SHADOWS = [
  'none',
  '0 1px 3px oklch(0% 0 0 / 0.12), 0 1px 2px oklch(0% 0 0 / 0.08)',
  '0 4px 8px oklch(0% 0 0 / 0.14), 0 2px 4px oklch(0% 0 0 / 0.08)',
  '0 12px 24px oklch(0% 0 0 / 0.18), 0 4px 8px oklch(0% 0 0 / 0.1)',
  '0 32px 64px oklch(0% 0 0 / 0.28), 0 12px 24px oklch(0% 0 0 / 0.14)',
];

const SHADOW_LABELS = ['None', 'XS', 'SM', 'MD', 'LG'];

const REGIONS = [
  { flag: 'us', label: 'US East — Ohio' },
  { flag: 'us', label: 'US West — Oregon' },
  { flag: 'ie', label: 'EU West — Ireland' },
  { flag: 'sg', label: 'AP — Singapore' },
];

const TABLE_ROWS = [
  { id: 'a1b2c3', name: 'Alice Chen',  flag: 'us', region: 'US East',    active: true  },
  { id: 'd4e5f6', name: 'Bob Davis',   flag: 'ie', region: 'EU West',    active: true  },
  { id: 'g7h8i9', name: 'Carol Kim',   flag: 'sg', region: 'AP',         active: false },
  { id: 'j0k1l2', name: 'Dave Lee',    flag: 'us', region: 'US West',    active: true  },
  { id: 'm3n4o5', name: 'Eva Müller',  flag: 'de', region: 'EU Central', active: true  },
  { id: 'p6q7r8', name: 'Frank Osei',  flag: 'za', region: 'AF South',   active: false },
  { id: 's9t0u1', name: 'Grace Park',  flag: 'jp', region: 'AP North',   active: true  },
  { id: 'v2w3x4', name: 'Hiro Tanaka', flag: 'jp', region: 'AP North',   active: true  },
  { id: 'y5z6a7', name: 'Isla Reyes',  flag: 'us', region: 'US East',    active: false },
];

const METRIC_DATA = [
  1820, 2100, 2340, 2180, 2560, 2890, 2750, 2100, 1940, 2210, 2480, 2230, 2050,
  2420, 2680, 2450, 2250, 2480, 2200, 2490, 2620, 2710, 2490, 2847,
];

function mk(tag, cls) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export function createThemeSandbox(props, index) {
  try {
    if (!window.matchMedia(DESKTOP_QUERY).matches) return null;

    const panel = mk(
      'div',
      'portfolio-panel portfolio-panel--component portfolio-panel--no-border',
    );
    panel.classList.remove('skeleton-loading');

    buildScene(panel);
    setTimeout(
      () => panel.classList.add('animated'),
      PANEL_ANIMATION_DELAY_MS * (index || 0),
    );
    return panel;
  } catch (err) {
    console.error('[theme-sandbox] render failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Scene
// ---------------------------------------------------------------------------

function buildScene(container) {
  const scene = mk('div', 'thb-scene');
  const state = { colorIdx: 0, fontIdx: 0, radius: 8, shadow: 1 };

  const preview = buildPreview();
  scene.appendChild(preview.el);

  const controls = buildControls(state, () => applyState(preview, state));
  scene.appendChild(controls);
  container.appendChild(scene);

  applyState(preview, state);
}

function applyState(
  { form, table, monitor, nav },
  { colorIdx, fontIdx, radius, shadow },
) {
  const color = SWATCHES[colorIdx].value;
  const onColor = SWATCHES[colorIdx].onColor;
  const r = radius + 'px';
  const sh = SHADOWS[shadow];
  const font = FONTS[fontIdx].value;

  // Form card
  form.card.style.fontFamily = font;
  form.card.style.borderRadius = r;
  form.card.style.boxShadow = sh;
  form.tag.style.borderRadius = r;
  form.tag.style.background = color;
  form.tag.style.color = onColor;
  form.inputs.forEach((inp) => {
    inp.style.borderRadius = r;
  });
  form.select.style.borderRadius = r;
  form.btn.style.borderRadius = r;
  form.btn.style.background = color;
  form.btn.style.color = onColor;

  // Table card
  table.card.style.fontFamily = font;
  table.card.style.borderRadius = r;
  table.card.style.boxShadow = sh;
  table.dots.forEach((dot) => {
    dot.style.background = color;
  });
  table.ids.forEach((id) => {
    id.style.color = color;
  });

  // Monitor card
  monitor.card.style.fontFamily = font;
  monitor.card.style.borderRadius = r;
  monitor.card.style.boxShadow = sh;
  monitor.dot.style.background = color;
  monitor.chart.s1.setAttribute('stop-color', color);
  monitor.chart.line.setAttribute('stroke', color);

  // Nav card
  nav.card.style.borderRadius = r;
  nav.card.style.boxShadow = sh;
  nav.avatar.style.background = color;
  nav.avatar.style.color = onColor;
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

function buildPreview() {
  const el = mk('div', 'thb-preview');
  const col = mk('div', 'thb-content-col');

  const nav = buildNavBar();
  col.appendChild(nav.el);

  const row = mk('div', 'thb-cards');
  const form = buildFormCard();
  const table = buildTableCard();
  const monitor = buildMonitorCard();

  row.appendChild(form.el);
  row.appendChild(table.el);
  row.appendChild(monitor.el);
  col.appendChild(row);
  el.appendChild(col);

  return { el, form, table, monitor, nav };
}

// ---------------------------------------------------------------------------
// Nav bar
// ---------------------------------------------------------------------------

const COMPANIES = [
  {
    name: 'Sky Co.',
    color: 'oklch(94.3% 0.029 294.588)',
    logo: '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="20" viewBox="0 0 46 40" fill="none" id="Logo"> <g id="logomark"> <path d="M0 33L4.60606 25H12.2448C17.2569 25 21.4947 28.7103 22.1571 33.6784L23 40H13L11.5585 36.6365C10.613 34.4304 8.44379 33 6.04362 33H0Z" fill="#6d28d9"></path> <path d="M46 33L41.3939 25H33.7552C28.7431 25 24.5053 28.7103 23.8429 33.6784L23 40H33L34.4415 36.6365C35.387 34.4304 37.5562 33 39.9564 33H46Z" fill="#6d28d9"></path> <path d="M4.60608 25L18.9999 0H23L22.6032 9.52405C22.2608 17.7406 15.7455 24.3596 7.53539 24.8316L4.60608 25Z" fill="#6d28d9"></path> <path d="M41.3939 25L27.0001 0H23L23.3968 9.52405C23.7392 17.7406 30.2545 24.3596 38.4646 24.8316L41.3939 25Z" fill="#6d28d9"></path> </g> </svg>',
  },
  {
    name: 'Orbit Labs',
    color: 'oklch(86.8% 0.007 39.5)',
    logo: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 80 80" fill="none" id="Logo"><g id="logomark"><path d="M44.0502 64.2231C44.122 56.8802 42.2604 50.909 39.8924 50.8858C37.5243 50.8628 35.5463 56.7965 35.4747 64.1393C35.4028 71.4821 37.2644 77.4535 39.6324 77.4765C42.0006 77.4997 43.9785 71.566 44.0502 64.2231Z" fill="black"></path><path d="M30.3144 62.5555C34.345 56.4173 36.0075 50.3875 34.028 49.0877C32.0483 47.7878 27.1762 51.7101 23.1458 57.8483C19.1152 63.9865 17.4526 70.0161 19.4322 71.3161C21.4118 72.616 26.2838 68.6937 30.3144 62.5555Z" fill="black"></path><path d="M19.6459 53.7487C26.3554 50.7644 31.0141 46.591 30.0515 44.4271C29.0891 42.2633 22.8699 42.9286 16.1605 45.913C9.45113 48.8974 4.79236 53.0708 5.75484 55.2346C6.71731 57.3985 12.9366 56.7332 19.6459 53.7487Z" fill="black"></path><path d="M29.2462 38.359C29.6065 36.0183 24.015 33.2153 16.7574 32.0982C9.49958 30.9811 3.324 31.9731 2.96374 34.3137C2.60348 36.6543 8.19497 39.4572 15.4527 40.5743C22.7103 41.6915 28.886 40.6995 29.2462 38.359Z" fill="black"></path><path d="M31.8522 32.7834C33.4205 31.009 30.2314 25.6283 24.7291 20.7655C19.2269 15.9026 13.4951 13.399 11.9268 15.1735C10.3585 16.948 13.5476 22.3287 19.0499 27.1915C24.5522 32.0544 30.284 34.5579 31.8522 32.7834Z" fill="black"></path><path d="M37.043 29.5246C39.3217 28.8798 39.5481 22.6291 37.5487 15.5634C35.5494 8.49766 32.0812 3.29248 29.8025 3.93729C27.5238 4.58211 27.2974 10.8327 29.2967 17.8985C31.2961 24.9641 34.7642 30.1694 37.043 29.5246Z" fill="black"></path><path d="M51.1336 18.1184C53.272 11.0935 53.1689 4.83958 50.9033 4.14996C48.6377 3.46033 45.0676 8.59611 42.9292 15.6211C40.791 22.6459 40.894 28.8999 43.1596 29.5895C45.4252 30.2791 48.9953 25.1434 51.1336 18.1184Z" fill="black"></path><path d="M61.2032 27.6106C66.7995 22.8562 70.0933 17.539 68.5598 15.7342C67.0265 13.9294 61.2469 16.3206 55.6507 21.0748C50.0544 25.8292 46.7608 31.1465 48.2941 32.9513C49.8273 34.7561 55.607 32.365 61.2032 27.6106Z" fill="black"></path><path d="M64.5312 41.0431C71.8096 40.0693 77.4552 37.3772 77.1411 35.0298C76.8272 32.6826 70.6723 31.5692 63.3939 32.5428C56.1157 33.5165 50.4699 36.2087 50.784 38.556C51.0981 40.9032 57.2528 42.0168 64.5312 41.0431Z" fill="black"></path><path d="M73.9204 55.9056C74.9254 53.7612 70.3499 49.4968 63.7006 46.3806C57.0515 43.2644 50.8465 42.4768 49.8415 44.6211C48.8366 46.7656 53.4121 51.03 60.0614 54.1462C66.7105 57.2622 72.9155 58.05 73.9204 55.9056Z" fill="black"></path><path d="M59.9333 71.728C61.9381 70.4675 60.3947 64.4061 56.4861 58.1896C52.5774 51.9731 47.7835 47.9557 45.7787 49.2162C43.7739 50.4767 45.3173 56.5381 49.2259 62.7546C53.1346 68.9711 57.9285 72.9887 59.9333 71.728Z" fill="black"></path></g></svg>',
  },
  {
    name: 'Meridian',
    color: 'oklch(48% 0.14 165)',
    logo: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 40 40" fill="none" id="Logo"><g id="logomark"><path d="M20 28.4356C16.5306 28.4357 13.3035 30.2166 11.4521 33.1512L9.1543 36.7934L10.0371 37.3511C10.3132 37.5101 10.5937 37.6622 10.8779 37.8082L13.1436 34.2188C14.6285 31.8648 17.2171 30.437 20 30.4369C22.783 30.4369 25.3714 31.8647 26.8564 34.2188L29.1211 37.8082C29.4053 37.6623 29.6858 37.5101 29.9619 37.3511L30.8457 36.7934L28.5479 33.1512C26.6964 30.2165 23.4695 28.4356 20 28.4356ZM20.123 36.4428L20 36.4379C19.3855 36.4381 18.8239 36.774 18.5312 37.3072L18.4766 37.4166L17.3691 39.834C18.0662 39.9256 18.7744 39.9821 19.4922 40L20 38.8914L20.5068 40C21.2249 39.9821 21.9335 39.9256 22.6309 39.834L21.5244 37.4166L21.4697 37.3072C21.1966 36.8096 20.689 36.4842 20.123 36.4428ZM21 12.4063V0H19V12.4063L15.7891 0.422921L13.8574 0.940585L17.0674 12.923L10.8662 2.18005L9.13379 3.18021L15.335 13.9222L6.56543 5.15124L5.15039 6.56651L13.9199 15.3375L3.17969 9.13529L2.17969 10.868L12.9209 17.0702L0.94043 13.8597L0.422852 15.7917L12.4053 19.0031H0V21.0035H12.4053L0.422852 24.2149L0.94043 26.1469L12.9209 22.9354L2.17969 29.1386L3.17969 30.8713L13.9199 24.6681L5.15039 33.4401L6.56543 34.8554L13.1216 28.2964C13.684 27.7337 14 26.9706 14 26.175V20.435C14 17.1209 16.6865 14.4342 20 14.434C23.3137 14.434 26 17.1208 26 20.435V26.1759C26 26.9716 26.316 27.7347 26.8785 28.2974L33.4346 34.8554L34.8496 33.4401L26.0801 24.6691L36.8203 30.8713L37.8203 29.1386L27.0791 22.9354L39.0596 26.1469L39.5771 24.2149L27.5957 21.0035H40V19.0031H27.5947L39.5771 15.7917L39.0596 13.8597L27.0791 17.0692L37.8203 10.868L36.8203 9.13529L26.0781 15.3375L34.8496 6.56554L33.4355 5.15124L24.6641 13.9232L30.8662 3.18021L29.1338 2.18005L22.9316 12.923L26.1426 0.940585L24.2109 0.422921L21 12.4063ZM20 32.4372C18.0122 32.4374 16.1792 33.5112 15.207 35.2453L13.2021 38.8201C13.8407 39.051 14.4942 39.2501 15.1611 39.4159L16.9512 36.224C17.5694 35.1209 18.7357 34.4377 20 34.4376C21.2645 34.4376 22.4305 35.1208 23.0488 36.224L24.8379 39.4159C25.5048 39.2502 26.1583 39.0509 26.7969 38.8201L24.793 35.2453C23.8208 33.5111 21.9879 32.4372 20 32.4372Z" fill="#ffffff"></path></g></svg>',
  },
  {
    name: 'Crestline',
    color: 'oklch(90.1% 0.058 230.902)',
    logo: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="20" viewBox="0 0 35 40" fill="none" id="Logo"> <g id="logomark"> <path fill-rule="evenodd" clip-rule="evenodd" d="M29.4554 2.43478V0H35V22.4348C35 32.1358 27.165 40 17.5 40C8.24271 40 0.664262 32.7853 0.0413736 23.6522H0V0H5.54455V2.43478H14.901V0H20.4455V2.43478H29.4554ZM29.4554 22.4348V19.0202C28.8318 19.6656 28.1633 20.2785 27.4539 20.8558C25.1121 22.7615 22.3612 24.2503 19.369 25.2589C16.3764 26.2677 13.1833 26.7826 9.96797 26.7826H6.35343C8.08848 31.2608 12.425 34.4348 17.5 34.4348C24.1028 34.4348 29.4554 29.0622 29.4554 22.4348ZM15.4269 18.2435C14.3706 19.3674 13.18 20.3419 11.8852 21.1425C13.8545 20.9882 15.7827 20.5971 17.6038 19.9833C20.013 19.1712 22.1698 17.9913 23.9621 16.5329C25.7535 15.075 27.136 13.3757 28.0645 11.5515C28.6507 10.3998 29.0518 9.20727 29.2674 8H20.2671C20.0641 9.47968 19.6891 10.9319 19.1475 12.3231C18.2893 14.5274 17.0275 16.5405 15.4269 18.2435ZM5.54455 17.8146V8H14.6483C14.4948 8.78546 14.2724 9.55482 13.9832 10.2975C13.3786 11.8506 12.4962 13.2517 11.3938 14.4246C10.2918 15.5971 8.99228 16.518 7.57404 17.143C6.91535 17.4333 6.23601 17.6576 5.54455 17.8146Z" fill="#1D3AA7"></path> </g> </svg>',
  },
];

const CHEVRON_UD_SVG =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 6.25L8 3.75L10.5 6.25" stroke="#9399A0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.5 9.75L8 12.25L10.5 9.75" stroke="#9399A0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const SEARCH_SVG =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7" cy="7" r="4.5" stroke="#71717A" stroke-width="1.5"/><line x1="10.5" y1="10.5" x2="14" y2="14" stroke="#71717A" stroke-width="1.5" stroke-linecap="round"/></svg>';

const BELL_SVG =
  '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 2.5a5 5 0 015 5V13H4V7.5a5 5 0 015-5Z" stroke="#71717A" stroke-width="1.5" stroke-linejoin="round"/><path d="M7 13v.5a2 2 0 004 0V13" stroke="#71717A" stroke-width="1.5"/><line x1="9" y1="1" x2="9" y2="2.5" stroke="#71717A" stroke-width="1.5" stroke-linecap="round"/></svg>';

function buildNavBar() {
  const el = mk('div', 'thb-nav');

  // Left
  const start = mk('div', 'thb-nav-start');

  const logo = mk('div', 'thb-nav-logo');
  logo.innerHTML = COMPANIES[0].logo;
  logo.style.background = COMPANIES[0].color;
  start.appendChild(logo);
  start.appendChild(buildOrgSelect(logo));

  const credits = mk('button', 'thb-nav-credits');
  credits.type = 'button';
  credits.innerHTML = '<span>Get free startup credits</span>';
  start.appendChild(credits);

  el.appendChild(start);

  // Center — real search input
  const searchWrap = mk('div', 'thb-nav-search');
  searchWrap.innerHTML = SEARCH_SVG;
  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.className = 'thb-nav-search-input';
  searchInput.placeholder = 'Search Astra DB';
  const kbd = mk('span', 'thb-nav-kbd');
  kbd.textContent = '⌘K';
  searchWrap.appendChild(searchInput);
  searchWrap.appendChild(kbd);
  el.appendChild(searchWrap);

  // Right
  const end = mk('div', 'thb-nav-end');
  const bell = mk('button', 'thb-nav-icon-btn');
  bell.type = 'button';
  bell.innerHTML = BELL_SVG;
  const avatar = mk('div', 'thb-nav-avatar');
  avatar.textContent = 'SC';
  end.appendChild(bell);
  end.appendChild(avatar);
  el.appendChild(end);

  return { el, card: el, avatar };
}

function buildOrgSelect(logoEl) {
  const wrapper = mk('div', 'thb-nav-org-wrap');
  const trigger = mk('button', 'thb-nav-org');
  trigger.type = 'button';
  const nameSpan = mk('span', 'thb-nav-org-name');
  nameSpan.textContent = COMPANIES[0].name;
  trigger.appendChild(nameSpan);
  trigger.insertAdjacentHTML('beforeend', CHEVRON_UD_SVG);

  const list = mk('ul', 'thb-nav-org-list');
  list.setAttribute('role', 'listbox');

  COMPANIES.forEach((company, i) => {
    const item = mk('li', 'thb-nav-org-item' + (i === 0 ? ' selected' : ''));
    item.setAttribute('role', 'option');
    item.textContent = company.name;
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      nameSpan.textContent = company.name;
      logoEl.innerHTML = company.logo;
      logoEl.style.background = company.color;
      list
        .querySelectorAll('.thb-nav-org-item')
        .forEach((n) => n.classList.remove('selected'));
      item.classList.add('selected');
      closeList();
    });
    list.appendChild(item);
  });

  wrapper.appendChild(trigger);
  wrapper.appendChild(list);

  function openList() {
    list.classList.add('open');
    setTimeout(() => document.addEventListener('click', onOutside), 0);
  }
  function closeList() {
    list.classList.remove('open');
    document.removeEventListener('click', onOutside);
  }
  function onOutside() {
    closeList();
  }
  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    list.classList.contains('open') ? closeList() : openList();
  });

  return wrapper;
}

// ---------------------------------------------------------------------------
// Form card — New database
// ---------------------------------------------------------------------------

function buildFormCard() {
  const el = mk('div', 'thb-card');

  const tag = mk('div', 'thb-tag');
  tag.textContent = 'Astra DB';

  const heading = mk('div', 'thb-heading');
  heading.textContent = 'New database';

  const dbField = buildField('Database name', 'input', {
    placeholder: 'cassandra-prod',
  });
  const regionField = buildRegionField('Region', REGIONS);
  const keyspaceField = buildField('Keyspace', 'input', {
    placeholder: 'main',
  });

  const btn = mk('button', 'thb-btn');
  btn.textContent = 'Create database';

  el.appendChild(tag);
  el.appendChild(heading);
  el.appendChild(dbField.el);
  el.appendChild(regionField.el);
  el.appendChild(keyspaceField.el);
  el.appendChild(btn);

  return {
    el,
    card: el,
    tag,
    inputs: [dbField.input, keyspaceField.input],
    select: regionField.select,
    btn,
  };
}

function makeFlagImg(flag) {
  const img = document.createElement('img');
  img.src = `https://flagicons.lipis.dev/flags/4x3/${flag}.svg`;
  img.alt = flag.toUpperCase();
  img.style.cssText = 'width:14px;height:10px;border-radius:1px;object-fit:cover;flex-shrink:0;';
  return img;
}

function buildRegionField(labelText, regions) {
  const field = mk('div', 'thb-field');
  const label = mk('label', 'thb-field-label');
  label.textContent = labelText;
  field.appendChild(label);

  const wrap = mk('div', 'thb-field-cselect-wrap');
  const btn = mk('button', 'thb-field-cselect-btn');
  btn.type = 'button';

  const display = mk('span', 'thb-field-cselect-display');
  display.appendChild(makeFlagImg(regions[0].flag));
  const labelSpan = mk('span', 'thb-field-cselect-label');
  labelSpan.textContent = regions[0].label;
  display.appendChild(labelSpan);
  btn.appendChild(display);
  btn.insertAdjacentHTML('beforeend', '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" style="flex-shrink:0"><path d="M2 3.5l3 3 3-3" stroke="#666" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');

  const list = mk('ul', 'thb-field-cselect-list');
  list.setAttribute('role', 'listbox');

  regions.forEach((region, i) => {
    const item = mk('li', 'thb-field-cselect-item' + (i === 0 ? ' selected' : ''));
    item.setAttribute('role', 'option');
    item.appendChild(makeFlagImg(region.flag));
    item.appendChild(document.createTextNode(region.label));
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      display.replaceChildren(makeFlagImg(region.flag));
      labelSpan.textContent = region.label;
      display.appendChild(labelSpan);
      list.querySelectorAll('.thb-field-cselect-item').forEach((n) => n.classList.remove('selected'));
      item.classList.add('selected');
      closeList();
    });
    list.appendChild(item);
  });

  wrap.appendChild(btn);
  wrap.appendChild(list);
  field.appendChild(wrap);

  function openList() {
    list.classList.add('open');
    setTimeout(() => document.addEventListener('click', onOutside), 0);
  }
  function closeList() {
    list.classList.remove('open');
    document.removeEventListener('click', onOutside);
  }
  function onOutside() { closeList(); }
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    list.classList.contains('open') ? closeList() : openList();
  });

  return { el: field, select: btn };
}

function buildField(labelText, type, opts = {}) {
  const field = mk('div', 'thb-field');
  const label = mk('label', 'thb-field-label');
  label.textContent = labelText;
  field.appendChild(label);

  if (type === 'select') {
    const select = document.createElement('select');
    select.className = 'thb-field-select';
    (opts.options || []).forEach((opt) => {
      const o = document.createElement('option');
      o.textContent = opt;
      select.appendChild(o);
    });
    field.appendChild(select);
    return { el: field, select };
  }

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'thb-input';
  if (opts.placeholder) input.placeholder = opts.placeholder;
  field.appendChild(input);
  return { el: field, input };
}

// ---------------------------------------------------------------------------
// Table card — users
// ---------------------------------------------------------------------------

function buildTableCard() {
  const el = mk('div', 'thb-card thb-card--data thb-card--table');

  const header = mk('div', 'thb-tc-header');
  const title = mk('span', 'thb-tc-title');
  title.textContent = 'users';
  const meta = mk('span', 'thb-tc-meta');
  meta.textContent = TABLE_ROWS.length + ' rows';
  header.appendChild(title);
  header.appendChild(meta);
  el.appendChild(header);

  const table = document.createElement('table');
  table.className = 'thb-tc-table';

  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  ['id', 'name', 'region', 'status'].forEach((col) => {
    const th = document.createElement('th');
    th.className = 'thb-tc-th';
    th.textContent = col;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  const dots = [];
  const ids = [];

  TABLE_ROWS.forEach((row) => {
    const tr = document.createElement('tr');
    tr.className = 'thb-tc-tr';

    const tdId = document.createElement('td');
    tdId.className = 'thb-tc-td';
    const idSpan = mk('span', 'thb-tc-id');
    idSpan.textContent = row.id;
    ids.push(idSpan);
    tdId.appendChild(idSpan);
    tr.appendChild(tdId);

    const tdName = document.createElement('td');
    tdName.className = 'thb-tc-td';
    tdName.textContent = row.name;
    tr.appendChild(tdName);

    const tdRegion = document.createElement('td');
    tdRegion.className = 'thb-tc-td thb-tc-td--muted';
    const regionWrap = mk('span', 'thb-tc-region');
    const flagImg = document.createElement('img');
    flagImg.src = `https://flagicons.lipis.dev/flags/4x3/${row.flag}.svg`;
    flagImg.className = 'thb-tc-flag';
    flagImg.alt = row.flag.toUpperCase();
    flagImg.style.cssText = 'width:16px;height:12px;border-radius:2px;object-fit:cover;flex-shrink:0;vertical-align:middle;';
    regionWrap.appendChild(flagImg);
    regionWrap.appendChild(document.createTextNode(row.region));
    tdRegion.appendChild(regionWrap);
    tr.appendChild(tdRegion);

    const tdStatus = document.createElement('td');
    tdStatus.className = 'thb-tc-td';
    const statusWrap = mk('span', 'thb-tc-status');
    const dot = mk('span', 'thb-tc-dot');
    if (row.active) dots.push(dot);
    else dot.classList.add('thb-tc-dot--inactive');
    statusWrap.appendChild(dot);
    statusWrap.appendChild(
      document.createTextNode(row.active ? 'Active' : 'Inactive'),
    );
    tdStatus.appendChild(statusWrap);
    tr.appendChild(tdStatus);

    tbody.appendChild(tr);
  });

  table.appendChild(tbody);
  el.appendChild(table);

  return { el, card: el, dots, ids };
}

// ---------------------------------------------------------------------------
// Monitor card — throughput sparkline
// ---------------------------------------------------------------------------

function buildMonitorCard() {
  const el = mk('div', 'thb-card thb-card--data thb-card--monitor');

  // Header
  const header = mk('div', 'thb-mc-header');
  const title = mk('span', 'thb-mc-title');
  title.textContent = 'Throughput';
  const badge = mk('span', 'thb-mc-badge');
  const dot = mk('span', 'thb-mc-dot');
  badge.appendChild(dot);
  badge.appendChild(document.createTextNode('Live'));
  header.appendChild(title);
  header.appendChild(badge);
  el.appendChild(header);

  // Metric
  const metric = mk('div', 'thb-mc-metric');
  const currentVal = METRIC_DATA[METRIC_DATA.length - 1];
  const prevVal = METRIC_DATA[METRIC_DATA.length - 2];
  const trendPct = (((currentVal - prevVal) / prevVal) * 100).toFixed(1);
  const trendUp = currentVal >= prevVal;

  const valEl = mk('div', 'thb-mc-value');
  valEl.textContent = currentVal.toLocaleString();
  metric.appendChild(valEl);

  const sub = mk('div', 'thb-mc-sub');
  const unit = mk('span', 'thb-mc-unit');
  unit.textContent = 'ops / sec';
  const trend = mk(
    'span',
    'thb-mc-trend' + (trendUp ? '' : ' thb-mc-trend--down'),
  );
  trend.textContent = (trendUp ? '▲' : '▼') + ' ' + Math.abs(trendPct) + '%';
  sub.appendChild(unit);
  sub.appendChild(trend);
  metric.appendChild(sub);
  el.appendChild(metric);

  // Sparkline
  const chart = buildSparkline(METRIC_DATA);
  el.appendChild(chart.svg);

  return { el, card: el, dot, chart };
}

function buildSparkline(data) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const VW = 300,
    VH = 72,
    PAD = 8;

  const pts = data.map((v, i) => ({
    x: (i / (data.length - 1)) * VW,
    y: VH - PAD - ((v - min) / range) * (VH - PAD * 2),
  }));

  // Smooth cubic bezier through points
  let linePath = `M${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1],
      c = pts[i];
    const dx = (c.x - p.x) / 3;
    linePath += ` C${p.x + dx},${p.y} ${c.x - dx},${c.y} ${c.x},${c.y}`;
  }
  const areaPath = `${linePath} L${VW},${VH} L0,${VH}Z`;

  const NS = 'http://www.w3.org/2000/svg';

  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${VW} ${VH}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.style.cssText = `display:block;width:100%;height:${VH}px;`;

  const defs = document.createElementNS(NS, 'defs');
  const grad = document.createElementNS(NS, 'linearGradient');
  grad.id = 'thb-mc-grad';
  grad.setAttribute('x1', '0');
  grad.setAttribute('y1', '0');
  grad.setAttribute('x2', '0');
  grad.setAttribute('y2', '1');
  const s1 = document.createElementNS(NS, 'stop');
  s1.setAttribute('offset', '0%');
  s1.setAttribute('stop-opacity', '0.18');
  const s2 = document.createElementNS(NS, 'stop');
  s2.setAttribute('offset', '100%');
  s2.setAttribute('stop-opacity', '0');
  grad.append(s1, s2);
  defs.appendChild(grad);
  svg.appendChild(defs);

  const area = document.createElementNS(NS, 'path');
  area.setAttribute('d', areaPath);
  area.setAttribute('fill', 'url(#thb-mc-grad)');
  svg.appendChild(area);

  const line = document.createElementNS(NS, 'path');
  line.setAttribute('d', linePath);
  line.setAttribute('fill', 'none');
  line.setAttribute('stroke-width', '1.5');
  line.setAttribute('stroke-linecap', 'round');
  svg.appendChild(line);

  return { svg, s1, s2, line };
}

// ---------------------------------------------------------------------------
// Controls panel
// ---------------------------------------------------------------------------

function buildControls(state, onUpdate) {
  const panel = mk('div', 'thb-controls');

  const colorLabel = mk('div', 'thb-color-label');
  colorLabel.textContent = 'Color';
  panel.appendChild(colorLabel);

  const swatchRow = mk('div', 'thb-swatches');
  SWATCHES.forEach((swatch, i) => {
    const btn = mk('button', 'thb-swatch');
    if (i === state.colorIdx) btn.classList.add('active');
    btn.style.background = swatch.value;
    btn.setAttribute('aria-label', swatch.label);
    btn.addEventListener('click', () => {
      swatchRow
        .querySelectorAll('.thb-swatch')
        .forEach((s) => s.classList.remove('active'));
      btn.classList.add('active');
      state.colorIdx = i;
      onUpdate();
    });
    swatchRow.appendChild(btn);
  });
  panel.appendChild(swatchRow);

  panel.appendChild(buildFontRow(state, onUpdate));
  panel.appendChild(
    buildRangeRow(
      'Radius',
      0,
      24,
      state.radius,
      (v) => v + 'px',
      (v) => {
        state.radius = v;
        onUpdate();
      },
    ),
  );
  panel.appendChild(
    buildRangeRow(
      'Shadow',
      0,
      SHADOWS.length - 1,
      state.shadow,
      (v) => SHADOW_LABELS[v],
      (v) => {
        state.shadow = v;
        onUpdate();
      },
    ),
  );

  return panel;
}

// ---------------------------------------------------------------------------
// Font select row
// ---------------------------------------------------------------------------

function buildFontRow(state, onUpdate) {
  const row = mk('div', 'thb-row');
  const label = mk('span', 'thb-label');
  label.textContent = 'Font';
  row.appendChild(label);

  const wrapper = mk('div', 'thb-select-wrap');
  const trigger = mk('button', 'thb-select-btn');
  trigger.type = 'button';
  const labelSpan = mk('span', 'thb-select-text');
  labelSpan.textContent = FONTS[state.fontIdx].label;
  trigger.appendChild(labelSpan);

  const chevron = mk('span', 'thb-select-chevron');
  chevron.innerHTML =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M7 12.25L3.938 9.188L4.554 8.571L7 11.012L9.446 8.571L10.063 9.188L7 12.25Z" fill="currentColor"/>' +
    '<path d="M7 1.75L10.063 4.813L9.446 5.429L7 2.988L4.554 5.429L3.938 4.813L7 1.75Z" fill="currentColor"/>' +
    '</svg>';
  trigger.appendChild(chevron);

  const list = mk('ul', 'thb-select-list');
  list.setAttribute('role', 'listbox');

  FONTS.forEach((font, i) => {
    const item = mk(
      'li',
      'thb-select-item' + (i === state.fontIdx ? ' selected' : ''),
    );
    item.setAttribute('role', 'option');
    const span = mk('span', null);
    span.textContent = font.label;
    span.style.fontFamily = font.value;
    item.appendChild(span);
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      labelSpan.textContent = font.label;
      list
        .querySelectorAll('.thb-select-item')
        .forEach((el) => el.classList.remove('selected'));
      item.classList.add('selected');
      state.fontIdx = i;
      onUpdate();
      close();
    });
    list.appendChild(item);
  });

  wrapper.appendChild(trigger);
  wrapper.appendChild(list);
  row.appendChild(wrapper);

  function open() {
    list.classList.add('open');
    setTimeout(() => document.addEventListener('click', onOutside), 0);
  }
  function close() {
    list.classList.remove('open');
    document.removeEventListener('click', onOutside);
  }
  function onOutside() {
    close();
  }
  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    list.classList.contains('open') ? close() : open();
  });

  return row;
}

// ---------------------------------------------------------------------------
// Range row
// ---------------------------------------------------------------------------

function buildRangeRow(labelText, min, max, initial, formatter, onChange) {
  const row = mk('div', 'thb-row');
  const label = mk('span', 'thb-label');
  label.textContent = labelText;

  let curN = initial;
  const track = mk('div', 'thb-range-track');
  const fill = mk('div', 'thb-range-fill');
  const thumb = mk('div', 'thb-range-thumb');
  track.appendChild(fill);
  track.appendChild(thumb);

  const valueSpan = mk('span', 'thb-range-value');
  valueSpan.textContent = formatter(curN);

  function updatePos() {
    const pct = ((curN - min) / (max - min)) * 100;
    fill.style.width = pct + '%';
    thumb.style.left = pct + '%';
  }
  updatePos();

  function onMove(clientX) {
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    curN = Math.round(min + pct * (max - min));
    valueSpan.textContent = formatter(curN);
    updatePos();
    onChange(curN);
  }

  track.addEventListener('mousedown', (e) => {
    e.preventDefault();
    onMove(e.clientX);
    const onMouseMove = (e) => onMove(e.clientX);
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });

  row.appendChild(label);
  row.appendChild(track);
  row.appendChild(valueSpan);
  return row;
}
